/**
 * Der Zugang zum Server.
 *
 * Duenn mit Absicht: Die Typen kommen aus `@thw/contracts`, also aus derselben
 * Definition, aus der der Server seine Pruefung baut. Eine Abweichung faellt
 * beim Uebersetzen auf und nicht im Einsatz.
 */

import type { Benutzer, Fehler, Sitzung } from "@thw/contracts";

const SITZUNG_SCHLUESSEL = "thw-sitzung";

export class ServerFehler extends Error {
  override readonly name = "ServerFehler";
  readonly code: number;
  readonly felder: { pfad: string; meldung: string }[];

  constructor(meldung: string, code: number, felder: { pfad: string; meldung: string }[] = []) {
    super(meldung);
    this.code = code;
    this.felder = felder;
  }
}

interface GespeicherteSitzung {
  token: string;
  gueltig_bis: string;
  benutzer: Benutzer;
  geraet: string;
}

/**
 * Die Sitzung liegt im `localStorage`.
 *
 * Nicht in einem Cookie: Die Oberflaeche soll spaeter auch dann funktionieren,
 * wenn sie als installierte App von einer anderen Herkunft kommt. Und der
 * Benutzer soll angemeldet bleiben -- ein Handy, das sich an der Einsatzstelle
 * abmeldet, ist dort nicht wieder anzumelden.
 */
export function sitzungLesen(): GespeicherteSitzung | null {
  try {
    const roh = globalThis.localStorage.getItem(SITZUNG_SCHLUESSEL);
    if (roh === null) return null;
    const sitzung = JSON.parse(roh) as GespeicherteSitzung;
    if (Date.parse(sitzung.gueltig_bis) <= Date.now()) return null;
    return sitzung;
  } catch {
    return null;
  }
}

export function sitzungSpeichern(sitzung: Sitzung, geraet: string): void {
  globalThis.localStorage.setItem(SITZUNG_SCHLUESSEL, JSON.stringify({ ...sitzung, geraet }));
}

/** Nach einer Aenderung am eigenen Konto: den gemerkten Benutzer nachziehen. */
export function sitzungBenutzerAktualisieren(benutzer: Benutzer): void {
  const sitzung = sitzungLesen();
  if (sitzung === null) return;
  globalThis.localStorage.setItem(SITZUNG_SCHLUESSEL, JSON.stringify({ ...sitzung, benutzer }));
}

export function sitzungLoeschen(): void {
  globalThis.localStorage.removeItem(SITZUNG_SCHLUESSEL);
}

/**
 * Kennung dieses Geraets.
 *
 * Landet in jeder Buchung und entscheidet Gleichstaende bei der
 * Konfliktaufloesung. Sie bleibt dem Geraet erhalten, damit dieselbe
 * Entscheidung auch morgen noch gleich ausfaellt.
 */
export function geraeteKennung(): string {
  const vorhanden = globalThis.localStorage.getItem("thw-geraet");
  if (vorhanden !== null) return vorhanden;
  const neu = `geraet-${Math.random().toString(36).slice(2, 8)}`;
  globalThis.localStorage.setItem("thw-geraet", neu);
  return neu;
}

/**
 * Eine Anfrage an den Server.
 *
 * `zeitlimit_ms` bricht ab, wenn die Antwort zu lange ausbleibt -- im
 * Funkloch haengt eine Verbindung sonst minutenlang. Der Abbruch wirft einen
 * `DOMException` mit dem Namen `TimeoutError`.
 */
export async function anfrage<T>(
  verfahren: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  pfad: string,
  koerper?: unknown,
  zeitlimit_ms?: number,
): Promise<T> {
  const sitzung = sitzungLesen();
  const antwort = await fetch(pfad, {
    method: verfahren,
    ...(zeitlimit_ms === undefined ? {} : { signal: AbortSignal.timeout(zeitlimit_ms) }),
    headers: {
      ...(koerper === undefined ? {} : { "content-type": "application/json" }),
      ...(sitzung === null ? {} : { authorization: `Bearer ${sitzung.token}` }),
    },
    // Den Rumpf nur mitgeben, wenn es einen gibt: Ein `undefined` an dieser
    // Stelle ist etwas anderes als gar kein Feld.
    ...(koerper === undefined ? {} : { body: JSON.stringify(koerper) }),
  });

  if (antwort.status === 204) return undefined as T;

  const text = await antwort.text();
  const daten: unknown = text === "" ? null : JSON.parse(text);

  if (!antwort.ok) {
    const fehler = daten as Fehler | null;
    // Abgelaufene Sitzung: Merkmal wegwerfen, damit die Oberflaeche zur
    // Anmeldung fuehrt statt in eine Schleife zu laufen.
    if (antwort.status === 401) sitzungLoeschen();
    throw new ServerFehler(
      fehler?.fehler ?? `Der Server antwortete mit ${String(antwort.status)}`,
      antwort.status,
      fehler?.felder ?? [],
    );
  }

  return daten as T;
}
